import { Component, inject, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { AuthStore } from '../../../store/auth.store';
import { AuthService } from '../../../services/auth.service';
import { AppNameComponent } from '../../../core/components';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, AppNameComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private router = inject(Router);

  readonly user = this.authStore.user;
  readonly isAuthenticated = this.authStore.isAuthenticated;

  showContextMenu = false;
  isHeaderVisible = signal(true);
  private lastScrollY = 0;
  private scrollThreshold = 10;
  private hideScrollThreshold = 50;

  onProfileClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showContextMenu = !this.showContextMenu;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.showContextMenu = false;
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    const currentScrollY = window.scrollY;

    // Always show header if scrolled less than 50px
    if (currentScrollY <= this.hideScrollThreshold) {
      this.isHeaderVisible.set(true);
      this.lastScrollY = currentScrollY;
      return;
    }

    // Check if scroll difference is significant enough
    const scrollDifference = Math.abs(currentScrollY - this.lastScrollY);
    if (scrollDifference < this.scrollThreshold) {
      return;
    }

    // Determine scroll direction and show/hide header (only after 50px)
    if (currentScrollY > this.lastScrollY) {
      // Scrolling down - hide header
      this.isHeaderVisible.set(false);
    } else {
      // Scrolling up - show header
      this.isHeaderVisible.set(true);
    }

    this.lastScrollY = currentScrollY;
  }

  async logout() {
    try {
      await this.authService.logout();
      this.showContextMenu = false;
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  navigateToProfile() {
    this.showContextMenu = false;
    this.router.navigate(['/profile']);
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  navigateToAdmin() {
    this.router.navigate(['/admin']);
  }

  get isAdmin() {
    return this.authStore.isAdmin();
  }
}
